import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ALLOWED_TYPES = ['terms', 'privacy', 'cookies'];

const normaliseType = (rawType) => {
  if (!rawType) return null;
  const value = String(rawType).toLowerCase();
  return ALLOWED_TYPES.includes(value) ? value : null;
};

const buildTitleKey = (type) => `legal.${type}.title`;
const buildSubtitleKey = (type) => `legal.${type}.subtitle`;
const buildKickerKey = (type) => `legal.${type}.kicker`;
const buildLastUpdatedKey = (type) => `legal.${type}.lastUpdated`;
const buildSectionsKey = (type) => `legal.${type}.sections`;

export const LegalPage = ({ type: typeProp }) => {
  const { t } = useTranslation();
  const params = useParams();
  const resolvedType = useMemo(
    () => normaliseType(typeProp || params.type),
    [typeProp, params.type],
  );

  useEffect(() => {
    if (!resolvedType) return;
    document.title = t(`legal.${resolvedType}.seoTitle`);
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', t(`legal.${resolvedType}.seoDescription`));
  }, [t, resolvedType]);

  if (!resolvedType) {
    return (
      <div className="layout-shell py-16">
        <section className="editorial-panel">
          <span className="hero-kicker">{t('legal.notFound.kicker')}</span>
          <h1 className="mt-5 editorial-title text-[var(--color-text)]">{t('legal.notFound.title')}</h1>
          <p className="mt-4 text-sm leading-7 text-[var(--color-text-muted)]">{t('legal.notFound.body')}</p>
        </section>
      </div>
    );
  }

  const sections = t(buildSectionsKey(resolvedType), { returnObjects: true }) || [];

  return (
    <div className="layout-shell py-10 lg:py-14">
      <section className="hero-full-bleed min-h-0">
        <div className="hero-media-plane" />
        <div className="relative z-10 rounded-[2rem] border border-[var(--color-border)] bg-[rgba(6,12,10,0.66)] p-6 md:p-8">
          <span className="hero-kicker">{t(buildKickerKey(resolvedType))}</span>
          <h1 className="mt-5 editorial-title text-[var(--color-text)]">{t(buildTitleKey(resolvedType))}</h1>
          <p className="mt-5 max-w-3xl text-base leading-8 text-[var(--color-text-muted)]">
            {t(buildSubtitleKey(resolvedType))}
          </p>
          <p className="mt-4 text-xs uppercase tracking-[0.18em] text-primary-200">
            {t('legal.lastUpdatedLabel')} {t(buildLastUpdatedKey(resolvedType))}
          </p>
        </div>
      </section>

      <div className="mt-8 space-y-6">
        {sections.map((section) => (
          <article key={section.heading} className="editorial-panel">
            <h2 className="font-display text-3xl uppercase text-[var(--color-text)]">{section.heading}</h2>
            {section.paragraphs && section.paragraphs.map((paragraph) => (
              <p key={paragraph} className="mt-3 text-sm leading-7 text-[var(--color-text-muted)]">{paragraph}</p>
            ))}
            {section.bullets && section.bullets.length > 0 && (
              <ul className="mt-4 space-y-2 text-sm text-[var(--color-text-muted)]">
                {section.bullets.map((bullet) => (
                  <li key={bullet} className="rounded-xl border border-[var(--color-border)] bg-white/[0.02] px-3 py-2">
                    {bullet}
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}
      </div>

      <section className="mt-10 editorial-panel">
        <h2 className="font-display text-3xl uppercase text-[var(--color-text)]">{t('legal.contact.title')}</h2>
        <p className="mt-3 text-sm leading-7 text-[var(--color-text-muted)]">{t('legal.contact.body')}</p>
        <a
          href={`mailto:${t('legal.contact.email')}`}
          className="mt-4 inline-flex items-center text-sm font-semibold text-primary-200 hover:text-primary-100"
        >
          {t('legal.contact.email')}
        </a>
      </section>
    </div>
  );
};

export default LegalPage;
